<template>
  <div class="bc-white padding-20 br-4">
    <div class="display-flex ai-center jc-space-between">
      <b class="fs-18">推送更新信息</b>
      <el-button type="primary" icon="el-icon-plus" @click="add">新增推送更新信息</el-button>
    </div>

    <!-- 搜索 -->
    <div class="display-flex ai-center mt-20">
      <el-input
        v-model="searchForm.fuzzy"
        clearable
        placeholder="请输入更新主题"
        style="width: 220px"
        @input="handleFuzzyInput"
        @clear="handleSearch"
        @keyup.enter.native="handleSearch">
      </el-input>
      <el-date-picker
        v-model="dateRange"
        type="daterange"
        range-separator="至"
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        value-format="yyyy-MM-dd"
        class="ml-10"
        @change="handleSearch">
      </el-date-picker>
      <el-button class="ml-10" type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
      <el-button class="ml-10" icon="el-icon-refresh-left" @click="handleReset">重置</el-button>
    </div>

    <!-- 推送列表 -->
    <el-table
      ref="pushTable"
      class="mt-20"
      :data="tableData"
      v-loading="tableLoading"
      border
      :header-cell-style="{ background: '#F5F7FA', color: '#606266' }">
      <el-table-column prop="title" label="更新主题" min-width="150" show-overflow-tooltip>
        <template slot-scope="{ row }">
          <span class="mr-6">{{ row.title }}</span>
          <el-tag size="mini" :type="isPending(row) ? 'warning' : 'success'">
            {{ row.stateName || (isPending(row) ? '待发布' : '已发布') }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="content" label="更新正文" min-width="220" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ plainText(row.content) || '-' }}</template>
      </el-table-column>
      <el-table-column label="时间范围" width="190" align="center">
        <template slot-scope="{ row }">
          <template v-if="row.startTime && row.endTime">
            {{ row.startTime }} 至 {{ row.endTime }}
          </template>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column label="影响租户" min-width="180" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ scopeText(row) }}</template>
      </el-table-column>
      <el-table-column label="相关图片" min-width="130" show-overflow-tooltip>
        <template slot-scope="{ row }">
          <span :class="{ 'color-info': !(row.imgNameList && row.imgNameList.length) }">
            {{ row.imgNameList && row.imgNameList.length ? row.imgNameList.join('、') : '-' }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="createAt" label="创建时间" width="160" align="center"></el-table-column>
      <el-table-column prop="publisherName" label="发布人" width="100" align="center">
        <template slot-scope="{ row }">{{ row.publisherName || '-' }}</template>
      </el-table-column>
      <el-table-column label="操作" width="200" align="center">
        <template slot-scope="{ row }">
          <!-- 发送到用户端/修改/删除均仅待发布可操作 -->
          <template v-if="isPending(row)">
            <el-button type="text" icon="el-icon-promotion" class="color-primary" @click="publish(row)">发送到用户端</el-button>
            <el-button type="text" @click="edit(row)">修改</el-button>
            <el-button type="text" class="color-danger" @click="del(row)">删除</el-button>
          </template>
        </template>
      </el-table-column>
      <template slot="empty">
        <el-empty description="暂无推送更新信息" :image-size="80"></el-empty>
      </template>
    </el-table>

    <div class="display-flex jc-flex-end mt-16">
      <el-pagination
        background
        @current-change="handlePageChange"
        @size-change="handleSizeChange"
        :current-page="searchForm.pageNum"
        :page-size="searchForm.pageSize"
        :page-sizes="[10, 20, 50]"
        layout="total, prev, pager, next, sizes"
        :total="total">
      </el-pagination>
    </div>

    <!-- 新增/修改推送更新信息 -->
    <el-dialog
      :title="addDialog.isEdit ? '修改推送更新信息' : '新增推送更新信息'"
      :visible.sync="addDialog.show"
      width="640px"
      append-to-body>
      <el-form label-position="top" class="diy-top-form">
        <el-form-item label="更新主题" required>
          <el-input
            v-model="addDialog.form.title"
            maxlength="100"
            show-word-limit
            placeholder="请输入更新主题">
          </el-input>
        </el-form-item>
        <el-form-item label="更新正文">
          <wang-editor
            v-if="addDialog.show"
            ref="editor"
            height="200px"
            :default-html="addDialog.form.content"
            placeholder="请输入本次更新说明">
          </wang-editor>
        </el-form-item>
        <el-form-item label="预计时间范围" required>
          <el-date-picker
            v-model="addDialog.form.timeRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            style="width: 100%">
          </el-date-picker>
        </el-form-item>
        <el-form-item label="影响范围" required>
          <el-checkbox-group v-model="addDialog.form.scope">
            <el-checkbox :label="1">集团&amp;节点终端</el-checkbox>
            <el-checkbox :label="2">一般租户&amp;集团下属租户终端</el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="影响的独立部署用户">
          <p class="fs-12 color-info margin-0" style="line-height: 1.5">
            SaaS租户默认全部影响，请另外选择会影响的独立部署用户。
          </p>
          <div class="display-flex ai-center width-percent-100 mt-10">
            <el-select
              v-model="addDialog.form.localTenants"
              multiple
              filterable
              collapse-tags
              :disabled="selectAllLocal"
              placeholder="请选择会影响的独立部署用户"
              style="flex: 1">
              <el-option
                v-for="item in localTenantOptions"
                :key="item.id"
                :label="item.name"
                :value="item.id">
              </el-option>
            </el-select>
            <el-checkbox class="ml-10" v-model="selectAllLocal" @change="toggleAllLocal">全选</el-checkbox>
          </div>
        </el-form-item>
        <el-form-item label="版本号">
          <el-input v-model="addDialog.form.version" maxlength="50" placeholder="例如：v1.0.0"></el-input>
        </el-form-item>
        <el-form-item label="相关图片">
          <el-upload
            action=""
            list-type="picture-card"
            accept=".png,.jpg,.jpeg,.gif"
            :file-list="addDialog.form.imageList"
            :limit="9"
            :http-request="uploadImage"
            :on-remove="removeImage"
            :on-exceed="() => $message.warning('最多上传 9 张图片')">
            <i class="el-icon-plus"></i>
          </el-upload>
          <p class="fs-12 color-info margin-0">支持 PNG、JPG、JPEG、GIF，最多 9 张图片。</p>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="addDialog.show = false">取 消</el-button>
        <el-button type="primary" :loading="addDialog.loading" @click="submit">保 存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import WangEditor from '@/components/WangEditor.vue'

export default {
  name: 'PushUpdate', // 推送更新信息
  components: { WangEditor },
  props: {},
  data () {
    return {
      total: 0,
      tableData: [],
      tableLoading: false,
      searchTimer: null, // 搜索防抖
      dateRange: null,
      searchForm: {
        pageNum: 1,
        pageSize: 10,
        fuzzy: ''
      },
      // 独立部署用户选项（仅配置了服务地址的租户/集团）
      localTenantOptions: [],
      selectAllLocal: false,
      addDialog: {
        show: false,
        loading: false,
        isEdit: false,
        editId: null,
        form: {
          title: '',
          content: '',
          timeRange: null,
          scope: [],
          localTenants: [],
          version: '',
          imageList: []
        }
      }
    }
  },
  filters: {},
  watch: {},
  computed: {},
  methods: {
    /**
     * 富文本转纯文本（列表展示用）
     */
    plainText (html) {
      if (!html) return ''
      const div = document.createElement('div')
      div.innerHTML = html
      return div.textContent || ''
    },
    /**
     * 是否待发布（可操作）：1待发布 2已发布
     * 兼容 state 为字符串的情况；列表未返回 state 时用 stateName 兜底判断
     */
    isPending (row) {
      if (row.state !== undefined && row.state !== null && row.state !== '') {
        return Number(row.state) === 1
      }
      const name = row.stateName || ''
      return name.indexOf('已发布') === -1
    },
    /**
     * 影响租户列文案：影响范围 + 独立部署租户合并展示
     */
    scopeText (row) {
      const scopeNames = row.scopeNameList || []
      let tenantNames = row.independentTenantNameList || []
      if (row.independentAll === 1) tenantNames = ['全部独立部署租户']
      const all = scopeNames.concat(tenantNames)
      return all.length ? all.join('、') : '全部租户'
    },
    /**
     * 输入防抖，直接调用查询接口
     */
    handleFuzzyInput () {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.handleSearch()
      }, 400)
    },
    /**
     * 查询（重置到第一页）
     */
    handleSearch () {
      this.searchForm.pageNum = 1
      this.getList()
    },
    /**
     * 重置
     */
    handleReset () {
      this.searchForm.fuzzy = ''
      this.dateRange = null
      this.handleSearch()
    },
    /**
     * 翻页
     */
    handlePageChange (val) {
      this.searchForm.pageNum = val
      this.getList()
    },
    /**
     * 每页条数切换
     */
    handleSizeChange (val) {
      this.searchForm.pageSize = val
      this.searchForm.pageNum = 1
      this.getList()
    },
    /**
     * 推送更新信息分页列表：POST /push/message/list
     */
    getList () {
      this.tableLoading = true
      const params = {
        pageNum: this.searchForm.pageNum,
        pageSize: this.searchForm.pageSize,
        fuzzy: this.searchForm.fuzzy.trim()
      }
      if (this.dateRange && this.dateRange.length === 2) {
        params.createStartTime = this.dateRange[0]
        params.createEndTime = this.dateRange[1]
      }
      this.$api.pushMessage.getList(params).then(res => {
        this.tableLoading = false
        this.tableData = (res && res.list) || []
        this.total = (res && res.total) || 0
        // 数据渲染后重算表格布局，避免列宽陈旧导致表头表体错位
        this.$nextTick(() => {
          if (this.$refs.pushTable) this.$refs.pushTable.doLayout()
        })
      }).catch(() => {
        this.tableLoading = false
      })
    },
    /**
     * 重算表格布局（防抖）：容器宽度变化时列宽才能重新适配，
     * 否则侧栏收起/展开等场景会残留旧列宽导致横向滚动与表头错位
     */
    doTableLayout () {
      clearTimeout(this.layoutTimer)
      this.layoutTimer = setTimeout(() => {
        if (this.$refs.pushTable) this.$refs.pushTable.doLayout()
      }, 100)
    },
    /**
     * 窗口尺寸变化时重算表格布局，防止表头表体错位
     */
    handleResize () {
      this.doTableLayout()
    },
    /**
     * 监听表格容器尺寸变化（侧栏收起/展开等不触发 window resize 的场景）
     */
    observeTableResize () {
      const el = this.$refs.pushTable && this.$refs.pushTable.$el
      if (!el || typeof ResizeObserver === 'undefined') return
      this.tableResizeObserver = new ResizeObserver(() => {
        this.doTableLayout()
      })
      this.tableResizeObserver.observe(el)
    },
    /**
     * 表头滚动同步兜底：表体横向滚动时强制表头跟随，
     * 避免个别浏览器下 el-table 自身同步失效导致表头表体错位
     */
    syncHeaderScroll () {
      const el = this.$refs.pushTable && this.$refs.pushTable.$el
      if (!el) return
      const bodyWrapper = el.querySelector('.el-table__body-wrapper')
      const headerWrapper = el.querySelector('.el-table__header-wrapper')
      if (!bodyWrapper || !headerWrapper) return
      this.headerSyncHandler = () => {
        if (headerWrapper.scrollLeft !== bodyWrapper.scrollLeft) {
          headerWrapper.scrollLeft = bodyWrapper.scrollLeft
        }
      }
      bodyWrapper.addEventListener('scroll', this.headerSyncHandler, { passive: true })
      this.tableBodyWrapper = bodyWrapper
    },
    /**
     * 独立部署租户选项（仅配置了服务地址的租户/集团）
     */
    getIndependentTenants () {
      this.$api.pushMessage.independentTenants().then(res => {
        this.localTenantOptions = (res || []).map(item => {
          return { id: item.id, name: item.name }
        })
      }).catch(() => {})
    },
    /**
     * 默认时间范围：开始为当前时刻，结束为一小时后并向上取整至最近的半小时
     */
    defaultTimeRange () {
      const pad = n => String(n).padStart(2, '0')
      const fmt = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
      const start = new Date()
      start.setSeconds(0, 0)
      const end = new Date(start.getTime() + 60 * 60 * 1000)
      // 向上取整至最近的半小时（0 分或 30 分）
      const minutes = end.getMinutes()
      const remainder = minutes % 30
      if (remainder !== 0) {
        end.setMinutes(minutes + (30 - remainder), 0, 0)
      }
      return [fmt(start), fmt(end)]
    },
    /**
     * 新增
     */
    add () {
      this.addDialog.isEdit = false
      this.addDialog.editId = null
      this.addDialog.form = {
        title: '',
        content: '',
        timeRange: this.defaultTimeRange(),
        scope: [2], // 默认勾选：2一般租户&集团下属租户终端
        localTenants: [],
        version: '',
        imageList: []
      }
      this.selectAllLocal = false
      this.addDialog.show = true
    },
    /**
     * 修改（仅待发布状态）：按行数据回填
     */
    edit (row) {
      this.addDialog.isEdit = true
      this.addDialog.editId = row.id
      const imgKeys = row.imgKeyList || []
      const imgNames = row.imgNameList || []
      const imgUrls = row.imgUrlList || []
      this.addDialog.form = {
        title: row.title || '',
        content: row.content || '',
        timeRange: row.startTime && row.endTime ? [row.startTime, row.endTime] : null,
        scope: (row.scopeList || []).slice(),
        localTenants: (row.independentTenantIdList || []).slice(),
        version: row.versionNo || '',
        imageList: imgKeys.map((key, index) => {
          return { key, name: imgNames[index] || key, url: imgUrls[index] || '' }
        })
      }
      this.selectAllLocal = row.independentAll === 1
      this.addDialog.show = true
    },
    /**
     * 发送到用户端（推送到各租户所有用户的站内信）
     */
    publish (row) {
      this.$confirm(`确认将「${row.title}」发送到用户端吗？将推送到各租户所有用户的站内信。`, '发送到用户端', {
        confirmButtonText: '确认发送',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$api.pushMessage.publish({ id: row.id }).then(() => {
          this.$message.success('发送成功')
          this.getList()
        })
      }).catch(() => {})
    },
    /**
     * 删除（仅待发布状态）
     */
    del (row) {
      this.$confirm(`确认删除「${row.title}」吗？`, '删除推送更新信息', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$api.pushMessage.del({ id: row.id }).then(() => {
          this.$message.success('删除成功')
          this.getList()
        })
      }).catch(() => {})
    },
    /**
     * 独立部署用户全选：全选时无需再传租户 id 列表
     */
    toggleAllLocal (val) {
      if (val) this.addDialog.form.localTenants = []
    },
    /**
     * 上传相关图片
     */
    uploadImage (e) {
      const param = new FormData()
      param.append('file', e.file)
      this.$api.common.upload(param).then(res => {
        this.addDialog.form.imageList.push({
          name: e.file.name,
          url: res.url,
          key: res.key
        })
      }).catch(() => {})
    },
    /**
     * 移除相关图片
     */
    removeImage (file) {
      this.addDialog.form.imageList = this.addDialog.form.imageList.filter(item => item.url !== file.url)
    },
    /**
     * 保存：新增 POST /push/message/save，修改 PUT /push/message/update（仅待发布状态）
     */
    submit () {
      const form = this.addDialog.form
      if (!form.title.trim()) {
        this.$message.warning('请输入更新主题')
        return
      }
      if (!form.timeRange || form.timeRange.length !== 2) {
        this.$message.warning('请选择预计时间范围')
        return
      }
      if (!form.scope.length) {
        this.$message.warning('请选择影响范围')
        return
      }
      const editor = this.$refs.editor && this.$refs.editor.editor
      const content = editor ? editor.getHtml() : form.content
      const payload = {
        title: form.title.trim(),
        content,
        startTime: form.timeRange[0],
        endTime: form.timeRange[1],
        // 影响范围：1集团&节点终端 2一般租户&集团下属租户终端，可多选
        scopeList: form.scope,
        // 影响的独立部署用户是否全选：1全选（无需传租户列表） 0指定
        independentAll: this.selectAllLocal ? 1 : 0,
        independentTenantIdList: this.selectAllLocal ? [] : form.localTenants,
        versionNo: form.version.trim(),
        imgKeyList: form.imageList.map(item => item.key),
        imgNameList: form.imageList.map(item => item.name)
      }
      const request = this.addDialog.isEdit
        ? this.$api.pushMessage.update({ ...payload, id: this.addDialog.editId })
        : this.$api.pushMessage.add(payload)
      this.addDialog.loading = true
      request.then(() => {
        this.addDialog.loading = false
        this.$message.success(this.addDialog.isEdit ? '保存成功' : '新增成功')
        this.addDialog.show = false
        this.getList()
      }).catch(() => {
        this.addDialog.loading = false
      })
    }
  },
  beforeCreate () {
  },
  created () {
  },
  beforeMount () {
  },
  mounted () {
    this.getList()
    this.getIndependentTenants()
    window.addEventListener('resize', this.handleResize)
    this.$nextTick(() => {
      this.observeTableResize()
      this.syncHeaderScroll()
    })
  },
  beforeUpdate () {
  },
  updated () {
  },
  beforeDestroy () {
    clearTimeout(this.searchTimer)
    clearTimeout(this.layoutTimer)
    window.removeEventListener('resize', this.handleResize)
    if (this.tableResizeObserver) this.tableResizeObserver.disconnect()
    if (this.tableBodyWrapper && this.headerSyncHandler) {
      this.tableBodyWrapper.removeEventListener('scroll', this.headerSyncHandler)
    }
  },
  destroyed () {
  }
}
</script>

<style lang="scss" scoped>
::v-deep .el-upload--picture-card {
  width: 100px;
  height: 100px;
  line-height: 100px;
}
::v-deep .el-upload-list--picture-card .el-upload-list__item {
  width: 100px;
  height: 100px;
}
</style>
